import { useCallback, useEffect, useState } from "react";

import { campaignKey, promptsForOptIn } from "@/campaigns";
import { useAction } from "@/hooks/use-action";
import { useAppStore } from "@/stores";
import type { CampaignSchema } from "@/types";
import {
  SESSION_DISMISS_LIMIT,
  mayPromptForOptIn,
  recordOptInPromptDismissal,
  sessionDismissals,
} from "@/utils/opt-in-prompt-memory";

export type OptInPromptPhase = "idle" | "joining" | "joined" | "failed";

/** The "You're in!" beat before the layer collapses into the page. */
const JOINED_HOLD_MS = 1200;

/**
 * Decides whether the opt-in prompt covers this campaign's page, and runs its opt-in.
 *
 * It asks only when ALL hold:
 * - the campaign takes widget opt-ins and the player can still join (`promptsForOptIn`) —
 *   operator-API campaigns keep the plain button;
 * - the player's join state for it is KNOWN to be "not joined": an explicit `false` from the
 *   `campaign_progress` stream (or a rollback), never a missing entry — before the stream lands,
 *   or for a campaign that arrived after it, a player who is already in would see a flash;
 * - a player is identified, their earlier answers allow it (`opt-in-prompt-memory`: one "Not now"
 *   waits for a later session, two silence it, an opt-in ends it), and this session has not gone
 *   quiet after two "Not now"s across campaigns.
 *
 * It asks until it gets an ANSWER: leaving without answering (switching campaign, closing the
 * widget, Info / T&C) is not one, so it is there again next time. Within a visit it stays up until
 * the player answers, and closes on its own if they join elsewhere meanwhile.
 */
export function useOptInPrompt(
  campaign: CampaignSchema | null,
  /** The type's own answer to "can the player still join?" (`CampaignView.canJoin`): a drained
   *  Sprint pool is over before its status says so, and the prompt must not ask then. */
  joinable: boolean = true
) {
  const key = campaign ? campaignKey(campaign) : null;
  const playerHash = useAppStore((s) => s.playerHash);
  // `undefined` = not known yet; only an explicit `false` lets the prompt ask.
  const joined = useAppStore((s) =>
    key != null ? s.joinedById[key] : undefined
  );
  const { optIn } = useAction();

  const [visit, setVisit] = useState<{
    key: string;
    phase: OptInPromptPhase;
  } | null>(null);

  const eligible =
    campaign != null &&
    key != null &&
    playerHash != null &&
    joined === false &&
    joinable &&
    promptsForOptIn(campaign, false) &&
    mayPromptForOptIn(playerHash, key) &&
    sessionDismissals() < SESSION_DISMISS_LIMIT;

  // A new campaign on screen ends the previous visit; an eligible one starts its own.
  const mine = visit?.key === key ? visit : null;
  useEffect(() => {
    if (key == null) return;
    if (visit && visit.key !== key) setVisit(null);
    else if (!visit && eligible) setVisit({ key, phase: "idle" });
  }, [key, eligible, visit]);

  // Joined some other way, or no longer joinable (the pool drained, the status flipped) while
  // the prompt was waiting for an answer: it has nothing to ask.
  useEffect(() => {
    if (mine?.phase === "idle" && (joined === true || !joinable))
      setVisit(null);
  }, [mine?.phase, joined, joinable]);

  const active = mine != null;

  const join = useCallback(() => {
    if (!campaign || !key) return;
    setVisit({ key, phase: "joining" });
    optIn(campaign, {
      onSuccess: () => {
        setVisit((v) => (v?.key === key ? { key, phase: "joined" } : v));
        window.setTimeout(
          () => setVisit((v) => (v?.key === key ? null : v)),
          JOINED_HOLD_MS
        );
      },
      onFailure: () =>
        setVisit((v) => (v?.key === key ? { key, phase: "failed" } : v)),
    });
  }, [campaign, key, optIn]);

  const dismiss = useCallback(() => {
    if (playerHash && key) recordOptInPromptDismissal(playerHash, key);
    setVisit(null);
  }, [playerHash, key]);

  // After an answer the visit is cleared, and `eligible` is false from then on (the answer is
  // recorded, or joined), so it does not restart this session.
  return { active, phase: mine?.phase ?? "idle", join, dismiss };
}
